<template>
  <div class="d-flex flex-column justify-between pa-4 grey darken-4" :data-test-id="dataTestId">
    <div class="d-flex align-end gap-1 fill-height">
      <div class="d-flex flex-column justify-between fill-height w-100">
        <div>
          <h3>{{ title }}</h3>
          <p>
            <slot />
          </p>
        </div>
        <div class="d-flex gap-1 mt-auto">
          <div class="d-flex gap-1 w-100 mb-n4">
            <TextDatePicker
              v-if="withDateFilter && !withDateRange"
              v-model="dateFilter"
              :allowed-dates="allowedDates"
              class="date-text date-filter mt-0"
              data-test-id="date-filter"
              dense
              label=""
              :max="dateMax"
              :min="dateMin"
            />
            <TextDateRangePicker
              v-if="withDateRange"
              v-model="dateRangeFilter"
              :allowed-dates="allowedDates"
              class="date-text date-range-filter mt-0"
              data-test-id="date-filter"
              dense
              label=""
              :max="dateMax"
              :min="dateMin"
            />

            <VSelect
              v-if="withCompanyFilter"
              v-model="company"
              class="mt-0"
              clearable
              dense
              :error-messages="showCompanyRequiredError ? ['Please select a company'] : []"
              item-text="name"
              item-value="id"
              :items="companyOptions"
              placeholder="Select a company"
              @change="showCompanyRequiredError = false"
            />
          </div>
        </div>
      </div>
      <div class="d-flex flex-column gap-1">
        <VBtn
          v-for="source in sourcesWithExt"
          :key="source.url"
          :data-test-id="`report-download-${source.ext}`"
          small
          @click="download(source)"
        >
          {{ source.ext }}
          <VIcon small> mdi-download </VIcon>
        </VBtn>
        <VCheckbox v-if="withPrettyOption" v-model="pretty" class="d-inline-block" dense />
      </div>
    </div>
    <VAlert v-if="downloadErr" class="mt-4 mb-0" dense type="error">
      {{ downloadErr }}
    </VAlert>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import axios from 'axios';
import { errorString } from '@/utils/helpers/rest-response';
import { fileNameFromHeaders } from '@/utils/api/helpers';
import { Api } from '@/modules/broker-admin/types/statistics';
import { add as dateAdd, format, parse } from 'date-fns';
import { ClientConfig } from '@/utils/helpers/rest';
import TextDatePicker from '@/modules/common/components/TextDatePicker.vue';
import TextDateRangePicker from '@/modules/common/components/TextDateRangePicker.vue';
import { parseDate } from '@/utils/helpers/dates';
import { useCurrentTime } from '@/composables/current-time';

const dateFilterInclTodayFromFormat = 'HH:mm';

interface Source {
  url: string;
  defaultName: string;
  ext: string;
}

const props = defineProps<{
  title: string;
  sources: Source[];
  withCompanyFilter?: boolean;
  withDateFilter?: boolean;
  withDateRange?: boolean;
  dateFilterInclTodayFrom?: string;
  dateFilterInclNonWorkdays?: boolean;
  withPrettyOption?: boolean;
  dataTestId: string;
}>();

const { currentTimeUTC } = useCurrentTime();
const instance = getCurrentInstance()!;
const clientConfig = computed<ClientConfig>(() => instance.proxy!.$store.state.clientConfig!);

const downloadErr = ref<string | null>(null);
const company = ref<number | null>(null);
const showCompanyRequiredError = ref(false);
const companyOptions = ref<Api.StatsFilters['companies']>([]);
const selectedDateFilter = ref('');
const selectedDateRangeFilter = ref<string[]>([]);
const pretty = ref(false);

const sourcesWithExt = computed<Source[]>(() =>
  props.sources.map((source) => ({
    ...source,
    ext: source.defaultName.split('.').pop() ?? '',
  }))
);

const shouldDateFilterInclToday = computed<boolean>(() => {
  if (!props.dateFilterInclTodayFrom) {
    return false;
  }

  try {
    // calculate the cutOff based on the provided prop, but with the timezone
    // appended to ensure it's not in the user's timezone
    const cutOff = parse(
      `${props.dateFilterInclTodayFrom} ${clientConfig.value.timezoneOffset}`,
      `${dateFilterInclTodayFromFormat} X`,
      currentTimeUTC.value
    );

    return currentTimeUTC.value >= cutOff;
  } catch {
    return false;
  }
});

const dateMin = computed<Date>(() => dateAdd(currentTimeUTC.value, { years: -1 }));

const dateMax = computed<Date>(() => {
  let max = dateAdd(currentTimeUTC.value, { days: shouldDateFilterInclToday.value ? 0 : -1 });
  while (!allowedDates(max)) {
    max = dateAdd(max, { days: -1 });
  }
  return max;
});

const dateFilter = computed({
  get(): Date {
    if (selectedDateFilter.value != '') {
      return parseDate(selectedDateFilter.value);
    }
    return dateMax.value;
  },
  set(date: Date | null) {
    selectedDateFilter.value = format(date ?? dateMax.value, 'yyyy-MM-dd');
  },
});

const dateRangeFilter = computed({
  get(): Date[] {
    if (selectedDateFilter.value != '') {
      return [parseDate(selectedDateFilter.value), dateMax.value];
    }
    return [dateMax.value, dateMax.value];
  },
  set(dateRange: Date[] | null) {
    const from = dateRange ? dateRange[0] : dateMax.value;
    const to = dateRange && dateRange[1] ? dateRange[1] : from;
    selectedDateRangeFilter.value = [format(from, 'yyyy-MM-dd'), format(to, 'yyyy-MM-dd')];
  },
});

function allowedDates(date: string | Date): boolean {
  if (props.dateFilterInclNonWorkdays) {
    return true;
  }
  const isWeekend = [0, 6].includes((date instanceof Date ? date : parseDate(date)).getDay());
  const isHoliday = clientConfig.value.holidaysLast24Months.includes(
    typeof date === 'string' ? date : format(date, 'yyyy-MM-dd')
  );
  return !isWeekend && !isHoliday;
}

async function loadFilters(): Promise<void> {
  try {
    const { data } = await axios.get<Api.StatsFilters>(
      `/api/1/broker-user/reports/filters/${props.dataTestId}`
    );
    companyOptions.value = data.companies;
  } catch {
    companyOptions.value = [];
  }
}

async function download(source: Source): Promise<void> {
  showCompanyRequiredError.value = false;
  downloadErr.value = null;

  if (props.withCompanyFilter && company.value === null) {
    showCompanyRequiredError.value = true;
    return;
  }

  try {
    const response = await axios.get<BlobPart>(source.url, {
      params: {
        company: props.withCompanyFilter ? company.value : undefined,
        // use 'date' for reports that target a specific date
        date: props.withDateFilter ? selectedDateFilter.value : undefined,
        // use 'from' and 'to' for reports that target a date range
        dateFrom:
          props.withDateRange && selectedDateRangeFilter.value.length > 0
            ? selectedDateRangeFilter.value[0]
            : undefined,
        dateTo:
          props.withDateRange && selectedDateRangeFilter.value.length > 1
            ? selectedDateRangeFilter.value[1]
            : undefined,
        pretty: pretty.value ? 'true' : undefined,
      },
      responseType: 'blob',
    });

    const fileName =
      fileNameFromHeaders(response.headers as Record<string, string>) || source.defaultName;
    const blob = new Blob([response.data], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (e) {
    downloadErr.value = errorString(e as Error);
  }
}

onMounted(async () => {
  if (props.withDateFilter) {
    dateFilter.value = dateMax.value;
  }
  if (props.withDateRange) {
    dateRangeFilter.value = [dateMax.value, dateMax.value];
  }
  if (props.withCompanyFilter) {
    await loadFilters();
  }
});
</script>

<style lang="scss" scoped>
.gap-1 {
  gap: 1rem;
}

.w-100 {
  width: 100%;
}

::v-deep {
  .date-text {
    flex-shrink: 0;
    flex-grow: 0;

    input {
      cursor: pointer;
    }
  }

  .date-filter {
    width: 8rem;
  }

  .date-range-filter {
    width: 14rem;
  }
}
</style>
